import { useMemo } from 'react';
import { CheckCircle2, ClipboardList, Download, LayoutGrid, Percent, Users } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { MetricTile } from '@/components/ui/MetricTile';
import { QueryError } from '@/components/ui/QueryError';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { Table, type Column } from '@/components/ui/Table';
import { gradeFor } from '@/constants/grading';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { downloadCsv } from '@/lib/download';
import { cn } from '@/lib/utils';
import type { ClassResultsStudent, ClassResultsSubject } from '@/types/exam';
import { teacherApi } from '../api/teacher.api';
import { examsApi } from '../api/exams.api';
import { examOriginFrom, formatInstituteDate, withExamOrigin } from '../utils';

/**
 * The class and exam on show, kept in the address bar, so going to the papers
 * and coming back finds the same results.
 *
 * `section` is empty until one is chosen — their first section stands in. An
 * empty `exam` means "the most recent exam", which the server picks. Both are
 * the server's to check: the section must be one they are class teacher of,
 * and an exam name is free text.
 */
type Filters = {
  section: string;
  exam: string;
};

const DEFAULT_FILTERS: Filters = {
  section: '',
  exam: '',
};

/**
 * Where a child stands on the exam as a whole, said separately from the grade.
 *
 * The grade is read off the overall percentage, so a child can sit at an A on
 * the total and still have failed a subject. Rather than bend the grade, the
 * failure is named here, subject by subject, so the two never get read as one.
 * Absences count as a failed paper, as the server counts them in
 * `failedSubjects`; "Incomplete" while anything is still to be entered, since a
 * pass with a sheet outstanding is a guess.
 */
function resultOf(
  student: ClassResultsStudent,
  subjects: readonly ClassResultsSubject[],
): { tone: 'success' | 'critical' | 'neutral'; label: string } {
  if (student.missing > 0) return { tone: 'neutral', label: 'Incomplete' };

  const failed = subjects.flatMap((subject, index) => {
    const mark = student.marks[index];
    if (mark?.isAbsent) return [`${subject.subject.name} (AB)`];
    return mark?.hasPassed === false ? [subject.subject.name] : [];
  });

  return failed.length === 0
    ? { tone: 'success', label: 'Pass' }
    : { tone: 'critical', label: `Fail: ${failed.join(', ')}` };
}

/**
 * A class teacher's results: every subject's marks for one exam of their own
 * class, side by side — what they review before the report cards go out.
 *
 * Read-only by design. Each subject's marks are entered on that paper's own
 * sheet, by whoever teaches it; being class teacher lets them see the whole
 * class's results, never write another teacher's marks. The server decides
 * who may read this (`NOT_CLASS_TEACHER`), and only sections they are class
 * teacher of are offered here.
 */
export function TeacherClassResultsPage() {
  /** Which exams tab sent them here, for the way back. */
  const [searchParams] = useSearchParams();
  const {
    data: allocations,
    isLoading: isLoadingClasses,
    error: classesError,
    refetch: refetchClasses,
  } = useApiQuery(() => teacherApi.myAllocations(), []);

  const sectionOptions = useMemo(
    () => [
      ...new Map(
        (allocations ?? [])
          .filter((allocation) => allocation.isClassTeacher)
          .map((allocation) => [
            allocation.classSectionUuid,
            { value: allocation.classSectionUuid, label: allocation.displayName },
          ]),
      ).values(),
    ],
    [allocations],
  );

  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS);
  const examName = filters.exam;

  // The sections arrive after mount; the first one stands in until one is
  // chosen, and for a section from an old link that is no longer theirs.
  const sectionUuid = sectionOptions.some((option) => option.value === filters.section)
    ? filters.section
    : (sectionOptions[0]?.value ?? '');

  const { data, isLoading, error, refetch } = useApiQuery(
    () =>
      sectionUuid
        ? examsApi.classResults(sectionUuid, examName || undefined)
        : Promise.resolve(null),
    [sectionUuid, examName],
  );
  // Only the answer for what is on screen — a failed switch keeps the old one in `data`.
  const results = data && data.classSection.uuid === sectionUuid ? data : null;

  const examOptions = useMemo(
    () =>
      (results?.examNames ?? []).map((exam) => ({
        value: exam.name,
        label: `${exam.name} · ${exam.origin === 'INSTITUTE' ? 'Institute' : 'Class test'}`,
      })),
    [results],
  );

  const pending = (results?.subjects ?? []).filter(
    (subject) => subject.enteredCount < subject.totalStudents,
  );
  const graded = (results?.students ?? []).filter((student) => student.percentage !== null);
  const classAverage =
    graded.length > 0
      ? Math.round((graded.reduce((sum, s) => sum + (s.percentage ?? 0), 0) / graded.length) * 10) /
        10
      : null;

  function exportResults() {
    if (!results?.selected) return;
    downloadCsv(
      `results-${results.selected.replace(/\s+/g, '-')}-${results.classSection.displayName.replace(/\s+/g, '-')}.csv`,
      [
        [
          'Roll No',
          'Admission No',
          'Student Name',
          ...results.subjects.map((subject) => `${subject.subject.name} (/${subject.maxMarks})`),
          `Total (/${results.maxTotal})`,
          'Percentage',
          'Grade',
          'Result',
        ],
        ...results.students.map((student) => [
          student.rollNumber,
          student.admissionNumber,
          student.fullName,
          ...student.marks.map((mark) =>
            mark.isAbsent ? 'AB' : mark.marksObtained === null ? '' : mark.marksObtained,
          ),
          // A sum with papers still to come is not a total.
          student.missing > 0 ? '' : student.total,
          student.percentage,
          gradeFor(student.percentage, 100)?.grade ?? '',
          resultOf(student, results.subjects).label,
        ]),
      ],
    );
  }

  const columns: Column<ClassResultsStudent>[] = [
    {
      key: 'student',
      header: 'Student',
      sticky: 'left',
      stickyWidth: 200,
      render: (student) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-ink">{student.fullName}</p>
          <p className="truncate text-xs text-ink-3 tabular-nums">
            {student.rollNumber ? `Roll ${student.rollNumber} · ` : ''}
            {student.admissionNumber}
          </p>
        </div>
      ),
    },
    ...(results?.subjects ?? []).map<Column<ClassResultsStudent>>((subject, index) => ({
      key: subject.examUuid,
      header: (
        <span className="flex flex-col leading-tight">
          <span>{subject.subject.name}</span>
          <span className="text-2xs font-medium text-ink-3">out of {subject.maxMarks}</span>
        </span>
      ),
      align: 'center',
      render: (student) => {
        const mark = student.marks[index];
        if (!mark || (!mark.isAbsent && mark.marksObtained === null)) {
          return (
            <span className="text-xs font-semibold text-ink-3" title="Not entered yet">
              —
            </span>
          );
        }
        if (mark.isAbsent) return <Badge tone="warning">AB</Badge>;
        return (
          <span
            className={cn(
              'text-sm font-bold tabular-nums',
              mark.hasPassed === false ? 'text-crit' : 'text-ink',
            )}
            title={mark.hasPassed === false ? 'Below the pass mark' : undefined}
          >
            {mark.marksObtained}
          </span>
        );
      },
    })),
    {
      key: 'total',
      header: `Total /${results?.maxTotal ?? 0}`,
      align: 'center',
      // Held back while a subject is pending, as the percentage is — a partial
      // sum beside the full "out of" reads as a low score.
      render: (student) =>
        student.missing > 0 ? (
          <span className="text-xs font-semibold text-ink-3" title="Not every subject is entered yet">
            —
          </span>
        ) : (
          <span className="text-sm font-bold tabular-nums text-ink">{student.total}</span>
        ),
    },
    {
      key: 'percentage',
      header: '%',
      align: 'center',
      render: (student) =>
        student.percentage === null ? (
          <span className="text-xs font-semibold text-ink-3">{student.missing} pending</span>
        ) : (
          <span className="text-sm font-bold tabular-nums text-ink">{student.percentage}</span>
        ),
    },
    {
      key: 'grade',
      header: 'Grade',
      align: 'center',
      render: (student) => {
        const band = gradeFor(student.percentage, 100);
        return band ? <Badge tone={band.tone}>{band.grade}</Badge> : null;
      },
    },
    {
      key: 'result',
      header: 'Result',
      render: (student) => {
        const result = resultOf(student, results?.subjects ?? []);
        return <Badge tone={result.tone}>{result.label}</Badge>;
      },
    },
  ];

  const breadcrumb = (
    <Breadcrumb
      // Back to the tab they came from — Institute tests stays Institute tests.
      items={[
        { label: 'Exam Marks', to: withExamOrigin('/teacher/marks', examOriginFrom(searchParams)) },
        { label: 'Class results' },
      ]}
    />
  );

  if (classesError) {
    return (
      <div className="flex flex-col gap-5">
        {breadcrumb}
        <QueryError
          title="Could not load your classes"
          error={classesError}
          onRetry={refetchClasses}
        />
      </div>
    );
  }

  if (!isLoadingClasses && sectionOptions.length === 0) {
    return (
      <div className="flex flex-col gap-5">
        {breadcrumb}
        <div className="card rounded-[16px] border border-line bg-surface p-5">
          <EmptyState
            icon={<LayoutGrid className="h-5 w-5" />}
            title="You are not a class teacher this session"
            description="Class results show every subject's marks for the class you are class teacher of. Ask the school office if you should have one."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {breadcrumb}

      <header className="card flex flex-col gap-3.5 rounded-[16px] border border-line bg-surface p-4 shadow-2xs lg:flex-row lg:items-end lg:justify-between lg:p-5">
        <div className="flex min-w-0 items-center gap-3.5">
          <span
            className="grid h-11 w-11 shrink-0 place-items-center rounded-[12px] border border-violet-500/25 bg-violet-500/15 text-violet-600 dark:text-violet-400 sm:h-12 sm:w-12"
            aria-hidden
          >
            <LayoutGrid className="h-5 w-5 sm:h-6 sm:w-6" />
          </span>
          <div className="min-w-0">
            <h1 className="text-xl font-bold tracking-tight text-ink sm:text-2xl">Class results</h1>
            <p className="mt-0.5 text-xs font-medium text-ink-3 sm:text-sm">
              Every subject's marks for your class, side by side — review them before the report
              cards go out.
            </p>
          </div>
        </div>

        <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end lg:w-auto lg:min-w-[560px]">
          <Select
            label="Class"
            options={sectionOptions}
            value={sectionUuid}
            disabled={isLoadingClasses}
            onChange={(event) => setFilters({ section: event.target.value, exam: '' })}
          />
          <Select
            label="Exam"
            options={examOptions}
            placeholder={examOptions.length === 0 ? 'No exams yet' : 'Choose an exam'}
            value={results?.selected ?? ''}
            disabled={examOptions.length === 0}
            onChange={(event) => setFilters({ exam: event.target.value })}
          />
          <Button
            variant="secondary"
            leftIcon={<Download className="h-4 w-4" />}
            onClick={exportResults}
            disabled={!results?.selected || results.students.length === 0}
          >
            Download
          </Button>
        </div>
      </header>

      {error ? (
        <QueryError title="Could not load the class results" error={error} onRetry={refetch} />
      ) : isLoading || isLoadingClasses || !results ? (
        <div className="flex flex-col gap-5" role="status" aria-label="Loading class results">
          <div className="grid grid-cols-2 gap-2.5 sm:gap-3.5 xl:grid-cols-4">
            {[0, 1, 2, 3].map((key) => (
              <Skeleton key={key} className="h-20 w-full rounded-card" />
            ))}
          </div>
          <Skeleton className="h-96 w-full rounded-card" />
        </div>
      ) : !results.selected ? (
        <div className="card rounded-[16px] border border-line bg-surface p-5">
          <EmptyState
            icon={<ClipboardList className="h-5 w-5" />}
            title="No exams published yet"
            description={`Once a paper is published for ${results.classSection.displayName}, its marks appear here subject by subject.`}
          />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2.5 sm:gap-3.5 xl:grid-cols-4">
            <MetricTile
              tone="violet"
              icon={<ClipboardList className="h-4 w-4 sm:h-5 sm:w-5" />}
              label="Subjects"
              value={results.subjects.length}
              hint={results.selected}
            />
            <MetricTile
              tone="sky"
              icon={<Users className="h-4 w-4 sm:h-5 sm:w-5" />}
              label="Students"
              value={results.students.length}
              hint={results.classSection.displayName}
            />
            <MetricTile
              tone="emerald"
              icon={<CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5" />}
              label="Subjects fully marked"
              value={results.subjects.length - pending.length}
              hint={`of ${results.subjects.length}`}
            />
            <MetricTile
              tone="amber"
              icon={<Percent className="h-4 w-4 sm:h-5 sm:w-5" />}
              label="Class average"
              value={classAverage === null ? '—' : `${classAverage}%`}
              hint={
                graded.length === results.students.length
                  ? 'Every student'
                  : `${graded.length} of ${results.students.length} with every mark in`
              }
            />
          </div>

          {results.isComplete ? (
            <Alert tone="success" title="Every mark is in">
              All {results.subjects.length} subjects are marked for every student. The results are
              ready for the report cards.
            </Alert>
          ) : (
            pending.length > 0 && (
              <Alert tone="warning" title="Some marks are still to come">
                <ul className="mt-1 flex flex-col gap-0.5">
                  {pending.map((subject) => (
                    <li key={subject.examUuid}>
                      {subject.subject.name} — {subject.enteredCount} of {subject.totalStudents}{' '}
                      entered, set for {formatInstituteDate(subject.examDate)} ({subject.subjectTeacher ?? 'Not allotted'})
                    </li>
                  ))}
                </ul>
              </Alert>
            )
          )}

          <Table
            columns={columns}
            rows={results.students}
            rowKey={(student) => student.uuid}
            empty={
              <EmptyState
                icon={<Users className="h-5 w-5" />}
                title="No students on this class"
                description="Students added to the class appear here with their marks."
              />
            }
          />
        </>
      )}
    </div>
  );
}
